import { useEffect, useMemo, useState } from 'react'
import { tournamentApi } from '../services/api'
import PortalShell from './setup/PortalShell'
import { useLanguage } from '../context/LanguageContext'

/**
 * Merchant-facing review moderation queue. Course-scoped via the JWT.
 * Reviews land here after the post-trip invite cron emails the customer
 * and they submit via /review/:token. They start with IsPublic=0 (hidden)
 * — merchant flips to IsPublic=1 to make them visible on the public widget.
 *
 * Three modes:
 *   - Pending (IsPublic=0): default, what needs moderation
 *   - All: full history including already-published / hidden
 */
function Stars({ rating, size = 16 }) {
  const r = Math.max(0, Math.min(5, Number(rating) || 0))
  return (
    <span style={{ display: 'inline-flex', gap: 2, color: '#f59e0b', fontSize: size }} aria-label={`${r} out of 5`}>
      {[1, 2, 3, 4, 5].map(i => <span key={i} style={{ opacity: i <= r ? 1 : 0.25 }}>★</span>)}
    </span>
  )
}

function fmtDateTime(d) {
  if (!d) return '—'
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  } catch { return '—' }
}

export default function Reviews() {
  const { t } = useLanguage()
  const [reviews, setReviews] = useState([])
  const [filter, setFilter] = useState('pending') // 'pending' | 'all'
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [busyIds, setBusyIds] = useState({}) // { [id]: 'publish' | 'hide' }

  function load() {
    setLoading(true); setError(null)
    const url = '/play-stay/reviews' + (filter === 'pending' ? '?onlyPending=1' : '')
    tournamentApi.get(url)
      .then(({ data }) => setReviews(Array.isArray(data) ? data : []))
      .catch(err => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false))
  }
  useEffect(load, [filter])

  async function moderate(id, isPublic) {
    setBusyIds(b => ({ ...b, [id]: isPublic ? 'publish' : 'hide' }))
    try {
      await tournamentApi.post(`/play-stay/reviews/${id}/moderate`, { isPublic })
      // Update locally to avoid a full refetch — keep already-loaded order
      setReviews(rs => rs.map(r => r.ID_NO === id
        ? { ...r, IsPublic: isPublic, ModeratedAt: new Date().toISOString() }
        : r
      ))
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Moderation failed')
    } finally {
      setBusyIds(b => { const next = { ...b }; delete next[id]; return next })
    }
  }

  const stats = useMemo(() => {
    const total = reviews.length
    const published = reviews.filter(r => r.IsPublic === 1).length
    const hidden = total - published
    const avg = total === 0 ? 0 : reviews.reduce((sum, r) => sum + Number(r.Rating || 0), 0) / total
    return { total, published, hidden, avg }
  }, [reviews])

  return (
    <PortalShell>
      <div style={{ maxWidth: 1100, margin: '40px auto', padding: '0 20px' }}>
        <div style={{ marginBottom: 8 }}>
          <a href="/portal" style={{ fontSize: 13, color: 'var(--brand-600)', textDecoration: 'none' }}>← {t('dashboard', 'Dashboard')}</a>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24, gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: 'var(--brand-600)', textTransform: 'uppercase' }}>{t('sp_stay_and_play_caps', 'Stay & Play')}</div>
            <h1 style={{ fontSize: 28, color: 'var(--secondary-950)', margin: '4px 0 0' }}>{t('sp_nav_reviews', 'Reviews')}</h1>
            <p style={{ color: 'var(--text-muted)', margin: '4px 0 0', fontSize: 14 }}>
              {t('sp_reviews_subtitle', 'Customers submit reviews 7 days after their trip. Approve to publish on the public widget; hide to keep private.')}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <FilterButton active={filter === 'pending'} onClick={() => setFilter('pending')}>{t('sp_filter_pending', 'Pending')}</FilterButton>
            <FilterButton active={filter === 'all'} onClick={() => setFilter('all')}>{t('all', 'All')}</FilterButton>
          </div>
        </div>

        {/* Stats strip — only meaningful in "all" view */}
        {filter === 'all' && reviews.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
            <Tile label={t('total', 'Total')} value={stats.total} />
            <Tile label={t('sp_reviews_published', 'Published')} value={stats.published} color="#16a34a" />
            <Tile label={t('sp_reviews_hidden', 'Hidden')} value={stats.hidden} color="#64748b" />
            <Tile label={t('sp_reviews_avg_rating', 'Avg rating')} value={stats.avg.toFixed(1) + ' ★'} color="#f59e0b" />
          </div>
        )}

        {error && (
          <div style={{ padding: 16, borderRadius: 8, background: '#fef2f2', color: '#991b1b', marginBottom: 16, border: '1px solid #fecaca' }}>{error}</div>
        )}

        {loading && (
          <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>{t('sp_loading_reviews', 'Loading reviews…')}</div>
        )}

        {!loading && reviews.length === 0 && (
          <div className="card" style={{ padding: 60, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }} aria-hidden="true">⭐</div>
            <h3 style={{ margin: '0 0 8px', color: 'var(--secondary-950)' }}>
              {filter === 'pending' ? t('sp_no_reviews_to_moderate', 'No reviews to moderate') : t('sp_no_reviews_yet', 'No reviews yet')}
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
              {filter === 'pending'
                ? t('sp_reviews_pending_empty', 'Once customers submit reviews, they show up here for approval.')
                : t('sp_reviews_all_empty', 'When customers complete their trips, they\'ll be invited to leave a review 7 days later.')}
            </p>
          </div>
        )}

        {!loading && reviews.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {reviews.map(r => (
              <ReviewCard
                key={r.ID_NO}
                review={r}
                busy={busyIds[r.ID_NO]}
                onPublish={() => moderate(r.ID_NO, 1)}
                onHide={() => moderate(r.ID_NO, 0)}
              />
            ))}
          </div>
        )}
      </div>
    </PortalShell>
  )
}

function ReviewCard({ review: r, busy, onPublish, onHide }) {
  const { t } = useLanguage()
  const isPublished = r.IsPublic === 1
  return (
    <div className="card" style={{ padding: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 8 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Stars rating={r.Rating} />
            <span style={{
              padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: 0.4,
              background: isPublished ? '#dcfce7' : '#fef3c7',
              color: isPublished ? '#166534' : '#92400e',
            }}>
              {isPublished ? t('sp_reviews_published', 'Published') : t('sp_reviews_hidden', 'Hidden')}
            </span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {r.GolferName || r.GolferEmail || t('sp_anonymous', 'Anonymous')} · {r.PackageName || '—'}
            <span style={{ marginLeft: 8 }}>· {t('sp_submitted', 'Submitted')} {fmtDateTime(r.SubmittedAt)}</span>
            {r.ModeratedAt && <span style={{ marginLeft: 8 }}>· {t('sp_moderated', 'Moderated')} {fmtDateTime(r.ModeratedAt)}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {!isPublished && (
            <button
              onClick={onPublish}
              disabled={!!busy}
              style={{
                padding: '6px 14px', fontSize: 13, fontWeight: 600,
                background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6,
                cursor: busy === 'publish' ? 'wait' : 'pointer',
              }}
            >
              {busy === 'publish' ? t('sp_publishing', 'Publishing…') : t('sp_publish_check', '✓ Publish')}
            </button>
          )}
          {isPublished && (
            <button
              onClick={onHide}
              disabled={!!busy}
              style={{
                padding: '6px 14px', fontSize: 13, fontWeight: 600,
                background: '#fff', color: '#374151', border: '1px solid #cbd5e1', borderRadius: 6,
                cursor: busy === 'hide' ? 'wait' : 'pointer',
              }}
            >
              {busy === 'hide' ? t('sp_hiding', 'Hiding…') : t('hide', 'Hide')}
            </button>
          )}
        </div>
      </div>

      {r.ReviewText && (
        <div style={{
          marginTop: 8, padding: '12px 14px', background: '#f8fafc', borderRadius: 6,
          fontSize: 14, color: 'var(--secondary-950)', lineHeight: 1.5,
          whiteSpace: 'pre-wrap', borderLeft: '3px solid #e5e7eb',
        }}>
          {r.ReviewText}
        </div>
      )}
    </div>
  )
}

function FilterButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '8px 14px', fontSize: 13, fontWeight: 600,
        background: active ? 'var(--secondary-950)' : '#fff',
        color: active ? '#fff' : 'var(--secondary-950)',
        border: '1px solid ' + (active ? 'var(--secondary-950)' : 'var(--card-border)'),
        borderRadius: 6, cursor: 'pointer',
      }}
    >
      {children}
    </button>
  )
}

function Tile({ label, value, color }) {
  return (
    <div className="card" style={{ padding: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || 'var(--secondary-950)', marginTop: 4 }}>{value}</div>
    </div>
  )
}
